import {
    Container,
    Typography,
    Box,
    CircularProgress,
    Card,
    CardContent,
    TextField,
    Stack,
    Button,
    Checkbox,
    Divider,
    FormControlLabel,
    List,
    ListItemButton,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Tooltip,
    InputAdornment,
    Tabs,
    Tab,
    Chip,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { authService } from '../api/auth';
import { User } from '../types/entities/user.entity';
import { useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LaunchIcon from '@mui/icons-material/Launch';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { getNotes } from '../api/notes';
import { taskService } from '../api/tasks';
import { projectsService } from '../api/projects';
import { pathOpenerItemService } from '../api/path-opener-items';
import { externalAssetService } from '../api/external-assets';
import { accountingService } from '../api/accounting';
import { Note } from '../types/note';
import { Task } from '../types/entities/task.entity';
import { ExternalAsset } from '../types/entities/external-asset.entity';
import { Accounting } from '../types/entities/accounting.entity';
import { DASHBOARD_CATALOG_LIMIT } from '../constants/dashboard';
import { formatAppDateTime } from '../utils/dateFormats';

const stripHtml = (html: string): string => (html ?? '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
const includesTerm = (value: string, term: string): boolean => value.toLowerCase().includes(term.toLowerCase());

const DASHBOARD_PREVIEW_SORT = 'sort[0]=updated_at:desc';
/** Max rows per API fetch and per Modules card list (no pagination — scroll inside card). */
const DASHBOARD_FETCH_LIMIT = 50;

const limitDashboardPreview = <T,>(items: T[]): T[] => items.slice(0, DASHBOARD_FETCH_LIMIT);

const sortByUpdatedAtDesc = <T extends { updated_at?: string | null }>(items: T[]): T[] =>
    [...items].sort((a, b) => parseUpdatedAtMs(b.updated_at) - parseUpdatedAtMs(a.updated_at));

type DashboardActivityKind =
    | 'note'
    | 'task'
    | 'project'
    | 'pathOpener'
    | 'externalAsset'
    | 'accounting';

type DashboardActivityItem = {
    kind: DashboardActivityKind;
    id: string;
    title: string;
    subtitle: string;
    updatedAt: number;
    href: string;
    pathOpenerValue?: string;
};

const parseUpdatedAtMs = (raw: string | null | undefined): number => {
    if (!raw) {
        return 0;
    }
    const ms = new Date(raw).getTime();
    return Number.isFinite(ms) ? ms : 0;
};

const catalogTopByUpdated = <T extends { updated_at?: string | null }>(items: T[]): T[] =>
    limitDashboardPreview(sortByUpdatedAtDesc(items));

const accountingHaystack = (a: Accounting): string =>
    [
        a.doc,
        a.description,
        a.author,
        a.account_debit_description,
        a.account_credit_description,
        a.exchange_currency,
        a.kind,
    ]
        .join(' ')
        .trim();

/** Same semantics as Notes list filters: $contains on DB columns (description includes raw HTML). */
const fetchDashboardNotesGlobal = async (term: string) => {
    const enc = encodeURIComponent(term);
    const [byName, byDesc] = await Promise.all([
        getNotes({
            page: 1,
            per_page: 50,
            filters: `filters[name][$contains]=${enc}`,
            sort: DASHBOARD_PREVIEW_SORT,
        }),
        getNotes({
            page: 1,
            per_page: 50,
            filters: `filters[description][$contains]=${enc}`,
            sort: DASHBOARD_PREVIEW_SORT,
        }),
    ]);
    const byId = new Map<string, Note>();
    for (const n of [...(byName.notes ?? []), ...(byDesc.notes ?? [])]) {
        if (!byId.has(n.id)) byId.set(n.id, n);
    }
    const notes = Array.from(byId.values()).sort(
        (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
    );
    return { notes, meta: byName.meta };
};

/** Same semantics as Tasks table filters on name / description (rich text stored as HTML). */
const fetchDashboardTasksGlobal = async (term: string) => {
    const enc = encodeURIComponent(term);
    const [byName, byDesc] = await Promise.all([
        taskService.getAll(1, 50, `filters[name][$contains]=${enc}`, DASHBOARD_PREVIEW_SORT),
        taskService.getAll(1, 50, `filters[description][$contains]=${enc}`, DASHBOARD_PREVIEW_SORT),
    ]);
    const byId = new Map<string, Task>();
    for (const t of [...(byName.tasks ?? []), ...(byDesc.tasks ?? [])]) {
        if (!byId.has(t.id)) byId.set(t.id, t);
    }
    const tasks = Array.from(byId.values()).sort(
        (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
    );
    return { tasks, meta: byName.meta };
};

const externalAssetHaystack = (a: ExternalAsset): string =>
    [
        a.title,
        a.file_path,
        a.publisher,
        a.source,
        a.asset_type,
        a.status,
        a.tags,
        a.projects,
        a.url,
        a.notes,
        ...(a.used_in_projects?.map((p) => `${p.name ?? ''} ${p.id ?? ''}`) ?? []),
    ]
        .join(' ')
        .trim();

/** Green/orange only while that card’s search is active (global or local); otherwise neutral. */
const previewCardSearchBorderSx = (searchActive: boolean, resultCount: number) => ({
    border: '2px solid',
    borderColor: searchActive ? (resultCount > 0 ? 'success.main' : 'warning.main') : 'divider',
});

/** Five module cards in one row (scroll on narrow viewports); full width when stacked. */
const previewCardRowSx = {
    flex: { xs: '0 0 auto', sm: '1 1 0' },
    minWidth: { xs: '100%', sm: 132 },
    width: { xs: '100%', sm: 'auto' },
};

/** Shared styles for each clickable preview row in dashboard module cards. */
const dashboardCardListSx = {
    display: 'flex',
    flexDirection: 'column',
    gap: 0.75,
    pt: 0.25,
} as const;

const dashboardCardListItemSx = {
    borderRadius: 1,
    border: '1px solid',
    borderColor: 'divider',
    py: 0.75,
    px: 1,
    '&:hover': {
        borderColor: 'primary.light',
        bgcolor: 'action.hover',
    },
} as const;

const isHttpUrl = (raw: string): boolean => /^https?:\/\//i.test(raw.trim());
const isWindowsPath = (raw: string): boolean => /^[A-Za-z]:\\/.test(raw.trim());
const toFileUrl = (windowsPath: string): string => {
    const trimmed = windowsPath.trim();
    const encoded = trimmed
        .replace(/\\/g, '/')
        .split('/')
        .map((segment) => encodeURIComponent(segment))
        .join('/');
    return `file:///${encoded}`;
};

const appRouteHref = (path: string): string =>
    `${window.location.origin}${path.startsWith('/') ? path : `/${path}`}`;

/** Real href for native link context menu (open/copy link). */
const pathOpenerHref = (raw: string): string => {
    const value = (raw ?? '').trim();
    if (!value) {
        return '#';
    }
    if (isHttpUrl(value)) {
        return value;
    }
    if (isWindowsPath(value)) {
        return toFileUrl(value);
    }
    return value;
};

const dashboardListLinkSx = {
    ...dashboardCardListItemSx,
    display: 'block',
    textDecoration: 'none',
    color: 'inherit',
};

/** Scrollable list inside a module card when showing up to 50 items. */
const dashboardModuleListScrollSx = {
    ...dashboardCardListSx,
    maxHeight: { xs: 320, sm: 420 },
    overflowY: 'auto',
} as const;

/** Recent activity tab — all loaded items, taller scroll area. */
const dashboardRecentActivityListSx = {
    ...dashboardCardListSx,
    maxHeight: { xs: '60vh', sm: '70vh' },
    overflowY: 'auto',
} as const;

const GLOBAL_SEARCH_RIGHT_CLICK_PASTE_KEY = 'dashboard.globalSearch.rightClickPaste';

function readGlobalSearchRightClickPaste(): boolean {
    try {
        const raw = localStorage.getItem(GLOBAL_SEARCH_RIGHT_CLICK_PASTE_KEY);
        if (raw === '0') return false;
        if (raw === '1') return true;
    } catch {
        /* ignore */
    }
    return true;
}

export const Dashboard = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { enqueueSnackbar } = useSnackbar();
    const [globalSearch, setGlobalSearch] = useState('');
    const [globalSearchRightClickPaste, setGlobalSearchRightClickPaste] = useState(readGlobalSearchRightClickPaste);
    const [notesSearch, setNotesSearch] = useState('');
    const [tasksSearch, setTasksSearch] = useState('');
    const [projectsSearch, setProjectsSearch] = useState('');
    const [pathOpenerSearch, setPathOpenerSearch] = useState('');
    const [externalAssetsSearch, setExternalAssetsSearch] = useState('');
    const [dashboardTab, setDashboardTab] = useState(0);
    const globalSearchInputRef = useRef<HTMLInputElement | null>(null);

    const focusGlobalSearchInput = () => {
        window.setTimeout(() => {
            globalSearchInputRef.current?.focus();
        }, 0);
    };

    const clearGlobalSearch = () => {
        setGlobalSearch('');
        focusGlobalSearchInput();
    };

    /**
     * Right-click on global search: if clipboard yields text, fill first line and suppress browser menu.
     * If read fails (permission / empty), default context menu stays (Paste, …). Some browsers may prompt for clipboard once per site.
     */
    const handleGlobalSearchContextMenu = async (e: MouseEvent<HTMLInputElement>) => {
        if (!globalSearchRightClickPaste) {
            return;
        }
        try {
            const raw = await navigator.clipboard.readText();
            const line =
                (raw ?? '')
                    .replace(/\r\n/g, '\n')
                    .split('\n')[0]
                    ?.trim() ?? '';
            if (!line) return;
            e.preventDefault();
            setGlobalSearch(line);
            focusGlobalSearchInput();
        } catch {
            /* keep native context menu */
        }
    };

    const handleGlobalSearchRightClickPasteChange = (checked: boolean) => {
        setGlobalSearchRightClickPaste(checked);
        try {
            localStorage.setItem(GLOBAL_SEARCH_RIGHT_CLICK_PASTE_KEY, checked ? '1' : '0');
        } catch {
            /* ignore */
        }
    };

    useEffect(() => {
        document.title = t('dashboard.title');
    }, []);

    const { data: user, isLoading, error } = useQuery<User>({
        queryKey: ['user'],
        queryFn: authService.getMe,
        retry: false
    });

    const notesQuery = useQuery({
        queryKey: ['dashboard-notes-preview', globalSearch.trim()],
        queryFn: async () => {
            const g = globalSearch.trim();
            if (!g) {
                return getNotes({
                    page: 1,
                    per_page: 50,
                    sort: DASHBOARD_PREVIEW_SORT,
                });
            }
            return fetchDashboardNotesGlobal(g);
        },
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
    });

    const tasksQuery = useQuery({
        queryKey: ['dashboard-tasks-preview', globalSearch.trim()],
        queryFn: async () => {
            const g = globalSearch.trim();
            if (!g) {
                return taskService.getAll(1, 50, '', DASHBOARD_PREVIEW_SORT);
            }
            return fetchDashboardTasksGlobal(g);
        },
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
    });

    const projectsQuery = useQuery({
        queryKey: ['dashboard-projects-preview', DASHBOARD_CATALOG_LIMIT],
        queryFn: () => projectsService.getCatalog({ limit: DASHBOARD_CATALOG_LIMIT }),
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
    });

    const pathOpenerQuery = useQuery({
        queryKey: ['dashboard-path-opener-preview', DASHBOARD_CATALOG_LIMIT],
        queryFn: () => pathOpenerItemService.getCatalog({ limit: DASHBOARD_CATALOG_LIMIT }),
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
    });

    const externalAssetsQuery = useQuery({
        queryKey: ['dashboard-external-assets-preview', DASHBOARD_CATALOG_LIMIT],
        queryFn: () => externalAssetService.getCatalog({ limit: DASHBOARD_CATALOG_LIMIT }),
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
    });

    const accountingQuery = useQuery({
        queryKey: ['dashboard-accounting-preview', globalSearch.trim()],
        queryFn: () => accountingService.getAll(1, 50, '', DASHBOARD_PREVIEW_SORT),
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
    });

    const filteredNotes = useMemo(() => {
        const notes = notesQuery.data?.notes ?? [];
        const g = globalSearch.trim();
        if (g) {
            return limitDashboardPreview(notes);
        }
        const term = notesSearch.trim();
        if (!term) {
            return limitDashboardPreview(notes);
        }
        return limitDashboardPreview(
            notes.filter(
                (n) => includesTerm(n.name ?? '', term) || includesTerm(stripHtml(n.description ?? ''), term),
            ),
        );
    }, [notesQuery.data?.notes, notesSearch, globalSearch]);

    const filteredTasks = useMemo(() => {
        const tasks = tasksQuery.data?.tasks ?? [];
        const g = globalSearch.trim();
        if (g) {
            return limitDashboardPreview(tasks);
        }
        const term = tasksSearch.trim();
        if (!term) {
            return limitDashboardPreview(tasks);
        }
        return limitDashboardPreview(
            tasks.filter(
                (task) =>
                    includesTerm(task.name ?? '', term) ||
                    includesTerm(stripHtml(task.description ?? ''), term),
            ),
        );
    }, [tasksQuery.data?.tasks, tasksSearch, globalSearch]);

    const filteredProjects = useMemo(() => {
        const g = globalSearch.trim();
        const term = g || projectsSearch.trim();
        const projects = catalogTopByUpdated(projectsQuery.data ?? []);
        if (!term) {
            return projects;
        }
        return limitDashboardPreview(
            projects.filter(
                (p) => includesTerm(p.name ?? '', term) || includesTerm(p.description ?? '', term),
            ),
        );
    }, [projectsQuery.data, projectsSearch, globalSearch]);

    const filteredPathOpenerItems = useMemo(() => {
        const g = globalSearch.trim();
        const term = g || pathOpenerSearch.trim();
        const entries = catalogTopByUpdated(pathOpenerQuery.data ?? []);
        if (!term) {
            return entries;
        }
        return limitDashboardPreview(
            entries.filter(
                (item) =>
                    includesTerm(item.value ?? '', term) ||
                    includesTerm(item.mode ?? '', term) ||
                    includesTerm(item.name ?? '', term) ||
                    includesTerm(item.description ?? '', term),
            ),
        );
    }, [pathOpenerQuery.data, pathOpenerSearch, globalSearch]);

    const filteredExternalAssets = useMemo(() => {
        const g = globalSearch.trim();
        const term = g || externalAssetsSearch.trim();
        const assets = catalogTopByUpdated(externalAssetsQuery.data ?? []);
        if (!term) {
            return assets;
        }
        return limitDashboardPreview(
            assets.filter((a) => includesTerm(externalAssetHaystack(a), term)),
        );
    }, [externalAssetsQuery.data, externalAssetsSearch, globalSearch]);

    const filteredAccountingsForActivity = useMemo(() => {
        const accountings = accountingQuery.data?.accountings ?? [];
        const g = globalSearch.trim();
        if (!g) {
            return limitDashboardPreview(accountings);
        }
        return limitDashboardPreview(accountings.filter((a) => includesTerm(accountingHaystack(a), g)));
    }, [accountingQuery.data?.accountings, globalSearch]);

    const recentActivityItems = useMemo((): DashboardActivityItem[] => {
        const g = globalSearch.trim();
        const items: DashboardActivityItem[] = [];

        const notes = notesQuery.data?.notes ?? [];
        for (const n of notes) {
            items.push({
                kind: 'note',
                id: String(n.id),
                title: n.name || `Note ${n.id}`,
                subtitle: stripHtml(n.description ?? '').slice(0, 96),
                updatedAt: parseUpdatedAtMs(n.updated_at),
                href: appRouteHref(`/notes/${n.id}/edit`),
            });
        }

        const tasks = tasksQuery.data?.tasks ?? [];
        for (const task of tasks) {
            items.push({
                kind: 'task',
                id: String(task.id),
                title: task.name || `Task ${task.id}`,
                subtitle: stripHtml(task.description ?? '').slice(0, 96),
                updatedAt: parseUpdatedAtMs(task.updated_at),
                href: appRouteHref(`/tasks?taskId=${encodeURIComponent(String(task.id))}`),
            });
        }

        const projectsBase = sortByUpdatedAtDesc(projectsQuery.data ?? []);
        const projectsForFeed = g
            ? projectsBase.filter(
                  (p) => includesTerm(p.name ?? '', g) || includesTerm(p.description ?? '', g),
              )
            : projectsBase;
        for (const p of projectsForFeed) {
            items.push({
                kind: 'project',
                id: String(p.id),
                title: p.name || `Project ${p.id}`,
                subtitle: (p.description ?? '').slice(0, 96),
                updatedAt: parseUpdatedAtMs(p.updated_at),
                href: appRouteHref(`/projects?projectId=${encodeURIComponent(String(p.id))}`),
            });
        }

        const pathEntries = sortByUpdatedAtDesc(pathOpenerQuery.data ?? []);
        const pathForFeed = g
            ? pathEntries.filter(
                  (item) =>
                      includesTerm(item.value ?? '', g) ||
                      includesTerm(item.mode ?? '', g) ||
                      includesTerm(item.name ?? '', g) ||
                      includesTerm(item.description ?? '', g),
              )
            : pathEntries;
        for (const item of pathForFeed) {
            const name = (item.name ?? '').trim();
            items.push({
                kind: 'pathOpener',
                id: String(item.id),
                title: name ? `${name} — ${item.value || `Entry ${item.id}`}` : item.value || `Entry ${item.id}`,
                subtitle: (item.description ?? '').trim().slice(0, 96) || `Mode: ${item.mode ?? 'Auto'}`,
                updatedAt: parseUpdatedAtMs(item.updated_at),
                href: pathOpenerHref(item.value),
                pathOpenerValue: item.value,
            });
        }

        const assetsBase = sortByUpdatedAtDesc(externalAssetsQuery.data ?? []);
        const assetsForFeed = g
            ? assetsBase.filter((a) => includesTerm(externalAssetHaystack(a), g))
            : assetsBase;
        for (const asset of assetsForFeed) {
            const secondaryParts = [asset.publisher, asset.asset_type, asset.file_path, asset.url]
                .map((s) => (s ?? '').trim())
                .filter(Boolean);
            items.push({
                kind: 'externalAsset',
                id: String(asset.id),
                title: asset.title?.trim() || `Asset ${asset.id}`,
                subtitle: secondaryParts.join(' · ').slice(0, 96) || '—',
                updatedAt: parseUpdatedAtMs(asset.updated_at),
                href: appRouteHref(`/external-assets?assetId=${encodeURIComponent(String(asset.id))}`),
            });
        }

        for (const a of filteredAccountingsForActivity) {
            const subtitleParts = [
                a.doc,
                a.description,
                a.author,
                a.kind === 'document_only' ? 'Document' : 'Booking',
            ]
                .map((s) => (s ?? '').toString().trim())
                .filter(Boolean);
            items.push({
                kind: 'accounting',
                id: String(a.id),
                title: a.doc?.trim() || a.description?.trim().slice(0, 60) || `Record ${a.id}`,
                subtitle: subtitleParts.join(' · ').slice(0, 96),
                updatedAt: parseUpdatedAtMs(a.updated_at),
                href: appRouteHref('/accounting'),
            });
        }

        return items.sort((a, b) => {
            if (b.updatedAt !== a.updatedAt) {
                return b.updatedAt - a.updatedAt;
            }
            return a.title.localeCompare(b.title);
        });
    }, [
        notesQuery.data?.notes,
        tasksQuery.data?.tasks,
        projectsQuery.data,
        pathOpenerQuery.data,
        externalAssetsQuery.data,
        filteredAccountingsForActivity,
        globalSearch,
    ]);

    const recentActivityLoading =
        notesQuery.isLoading ||
        tasksQuery.isLoading ||
        projectsQuery.isLoading ||
        pathOpenerQuery.isLoading ||
        externalAssetsQuery.isLoading ||
        accountingQuery.isLoading;

    const activityChipColor = (kind: DashboardActivityKind) => {
        switch (kind) {
            case 'note':
                return 'primary' as const;
            case 'task':
                return 'secondary' as const;
            case 'project':
                return 'info' as const;
            case 'pathOpener':
                return 'warning' as const;
            case 'externalAsset':
                return 'success' as const;
            case 'accounting':
                return 'default' as const;
        }
    };

    const activityTypeLabel = (kind: DashboardActivityKind) =>
        t(`dashboard.activityType.${kind}`);

    const anyPreviewModuleHasResults = useMemo(
        () =>
            filteredNotes.length > 0 ||
            filteredTasks.length > 0 ||
            filteredProjects.length > 0 ||
            filteredPathOpenerItems.length > 0 ||
            filteredExternalAssets.length > 0 ||
            filteredAccountingsForActivity.length > 0,
        [
            filteredNotes.length,
            filteredTasks.length,
            filteredProjects.length,
            filteredPathOpenerItems.length,
            filteredExternalAssets.length,
            filteredAccountingsForActivity.length,
        ],
    );

    const mainSearchSectionSx = useMemo(() => {
        const active = globalSearch.trim().length > 0;
        const base = {
            mt: 2,
            width: '100%',
            p: 2,
            borderRadius: 1,
            borderStyle: 'solid' as const,
            borderWidth: 2,
        };
        if (!active) {
            return {
                ...base,
                borderColor: 'divider',
                bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.14 : 0.06),
                boxShadow: theme.shadows[theme.palette.mode === 'dark' ? 3 : 2],
            };
        }
        if (anyPreviewModuleHasResults) {
            return {
                ...base,
                borderColor: 'success.main',
                bgcolor: alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.16 : 0.1),
                boxShadow: theme.shadows[3],
            };
        }
        return {
            ...base,
            borderColor: 'warning.main',
            bgcolor: alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.18 : 0.12),
            boxShadow: theme.shadows[3],
        };
    }, [globalSearch, anyPreviewModuleHasResults, theme]);

    const copyToClipboard = async (text: string) => {
        const value = (text ?? '').trim();
        if (!value) return;
        try {
            await navigator.clipboard.writeText(value);
            enqueueSnackbar('Copied to clipboard', { variant: 'success', autoHideDuration: 1200 });
        } catch {
            enqueueSnackbar('Copy failed', { variant: 'error' });
        }
    };

    const openPathOpenerValue = (raw: string) => {
        const value = (raw ?? '').trim();
        if (!value) return;

        if (isHttpUrl(value)) {
            window.open(value, '_blank', 'noopener,noreferrer');
            return;
        }

        if (isWindowsPath(value)) {
            // Best-effort: browsers may open Explorer for file:// URLs on Windows, but it's not guaranteed.
            window.open(toFileUrl(value), '_blank', 'noopener,noreferrer');
            return;
        }

        // Unknown scheme: treat as web-ish path (best effort)
        window.open(value, '_blank', 'noopener,noreferrer');
    };

    const handlePathOpenerLinkClick = (e: React.MouseEvent<HTMLAnchorElement>, raw: string) => {
        const value = (raw ?? '').trim();
        if (!value) {
            e.preventDefault();
            return;
        }

        // Windows paths: keep copy-on-click; right-click still gets file:// link menu.
        if (isWindowsPath(value)) {
            e.preventDefault();
            void copyToClipboard(value);
            enqueueSnackbar('Windows path copied — paste into Explorer address bar', { variant: 'info', autoHideDuration: 2200 });
        }
    };

    const searchClearEndAdornment = (value: string, onClear: () => void) =>
        value ? (
            <InputAdornment position="end">
                <IconButton
                    size="small"
                    edge="end"
                    aria-label={t('dashboard.clearSearch')}
                    onClick={(e) => {
                        e.preventDefault();
                        onClear();
                    }}
                >
                    <ClearIcon fontSize="small" />
                </IconButton>
            </InputAdornment>
        ) : undefined;

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
                <Typography color="error">{t('common.error')}</Typography>
            </Box>
        );
    }

    return (
        <Container
            maxWidth={false}
            sx={{
                mt: 4,
                width: '100%',
                maxWidth: '100%',
                px: { xs: 1, sm: 1.5 },
                boxSizing: 'border-box',
            }}
        >
            <Typography variant="h4" gutterBottom>
                {t('dashboard.welcome')}, {user?.name}
            </Typography>
            <Typography variant="body1">
                {user?.email}
            </Typography>

            <Box
                component="section"
                aria-label={t('dashboard.globalSearchSectionLabel')}
                sx={mainSearchSectionSx}
            >
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1, flexWrap: 'wrap' }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                        {t('dashboard.globalSearchSectionLabel')}
                    </Typography>
                    <Tooltip title={t('dashboard.clearSearch')}>
                        <span>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={clearGlobalSearch}
                                disabled={!globalSearch.trim()}
                                sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                            >
                                {t('dashboard.clearSearchButton')}
                            </Button>
                        </span>
                    </Tooltip>
                    <Tooltip title={t('dashboard.globalSearchRightClickPasteHint')}>
                        <FormControlLabel
                            sx={{ ml: { xs: 0, sm: 'auto' }, mr: 0 }}
                            control={
                                <Checkbox
                                    size="small"
                                    checked={globalSearchRightClickPaste}
                                    onChange={(_, checked) => handleGlobalSearchRightClickPasteChange(checked)}
                                />
                            }
                            label={(
                                <Typography variant="body2" color="text.secondary">
                                    {t('dashboard.globalSearchRightClickPaste')}
                                </Typography>
                            )}
                        />
                    </Tooltip>
                </Stack>
                <TextField
                    fullWidth
                    size="small"
                    inputRef={globalSearchInputRef}
                    inputProps={{ onContextMenu: handleGlobalSearchContextMenu }}
                    placeholder={t('dashboard.globalSearchPlaceholder')}
                    value={globalSearch}
                    onChange={(e) => setGlobalSearch(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" color="action" />
                            </InputAdornment>
                        ),
                        endAdornment: searchClearEndAdornment(globalSearch, clearGlobalSearch),
                    }}
                    helperText={globalSearch.trim() ? t('dashboard.globalSearchHelper') : ' '}
                />
            </Box>

            <Tabs
                value={dashboardTab}
                onChange={(_, value) => setDashboardTab(value)}
                sx={{ mt: 2 }}
                aria-label="Dashboard views"
            >
                <Tab label={t('dashboard.tabModules')} />
                <Tab label={t('dashboard.tabRecentActivity')} />
            </Tabs>

            {dashboardTab === 1 && (
                <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        {t('dashboard.recentActivityHelper', { count: recentActivityItems.length })}
                    </Typography>
                    {recentActivityLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress size={32} />
                        </Box>
                    ) : (
                        <List dense disablePadding sx={dashboardRecentActivityListSx}>
                            {recentActivityItems.map((item) => (
                                <ListItemButton
                                    key={`${item.kind}-${item.id}`}
                                    component="a"
                                    href={item.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={
                                        item.kind === 'pathOpener'
                                            ? { ...dashboardListLinkSx, pr: { xs: 8, sm: 9 } }
                                            : dashboardListLinkSx
                                    }
                                    onClick={
                                        item.kind === 'pathOpener' && item.pathOpenerValue
                                            ? (e) => handlePathOpenerLinkClick(e, item.pathOpenerValue!)
                                            : undefined
                                    }
                                >
                                    <ListItemText
                                        primary={
                                            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                                                <Chip
                                                    label={activityTypeLabel(item.kind)}
                                                    size="small"
                                                    color={activityChipColor(item.kind)}
                                                    sx={{ flexShrink: 0, fontWeight: 600, height: 22 }}
                                                />
                                                <Typography component="span" variant="body2" fontWeight={600} noWrap sx={{ minWidth: 0 }}>
                                                    {item.title}
                                                </Typography>
                                                {item.updatedAt > 0 && (
                                                    <Typography
                                                        component="span"
                                                        variant="caption"
                                                        color="text.secondary"
                                                        sx={{ flexShrink: 0, ml: 'auto' }}
                                                    >
                                                        {formatAppDateTime(item.updatedAt)}
                                                    </Typography>
                                                )}
                                            </Stack>
                                        }
                                        secondary={item.subtitle || '—'}
                                        secondaryTypographyProps={{ sx: { mt: 0.5 } }}
                                    />
                                    {item.kind === 'pathOpener' && item.pathOpenerValue ? (
                                        <ListItemSecondaryAction>
                                            <Tooltip title="Copy path">
                                                <IconButton
                                                    edge="end"
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        void copyToClipboard(item.pathOpenerValue!);
                                                    }}
                                                >
                                                    <ContentCopyIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </ListItemSecondaryAction>
                                    ) : null}
                                </ListItemButton>
                            ))}
                            {recentActivityItems.length === 0 && (
                                <Box sx={{ py: 2 }}>
                                    <Typography variant="body2" color="warning.main" fontWeight={500}>
                                        {t('dashboard.recentActivityEmpty')}
                                    </Typography>
                                </Box>
                            )}
                        </List>
                    )}
                </Box>
            )}

            {dashboardTab === 0 && (
            <Box
                sx={{
                    mt: 2,
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: 'stretch',
                    gap: 1.5,
                    overflowX: { sm: 'auto' },
                }}
            >
                <Card
                    variant="outlined"
                    sx={{
                        ...previewCardSearchBorderSx(
                            !!(globalSearch.trim() || notesSearch.trim()),
                            filteredNotes.length
                        ),
                        ...previewCardRowSx,
                    }}
                >
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1, gap: 0.5 }}>
                            <Typography variant="subtitle1" component="div" sx={{ fontWeight: 700, lineHeight: 1.2, minWidth: 0 }} noWrap>
                                Notes
                                {notesQuery.data?.meta != null && (
                                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75, fontWeight: 500 }}>
                                        ({filteredNotes.length}
                                        {notesQuery.data.meta.perPage < notesQuery.data.meta.total
                                            ? ` / ${notesQuery.data.meta.total}`
                                            : ''}
                                        )
                                    </Typography>
                                )}
                            </Typography>
                            <Button
                                component={RouterLink}
                                to="/notes"
                                size="small"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Open
                            </Button>
                        </Stack>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search notes (title + description)"
                            value={notesSearch}
                            onChange={(e) => setNotesSearch(e.target.value)}
                            InputProps={{
                                endAdornment: searchClearEndAdornment(notesSearch, () => setNotesSearch('')),
                            }}
                        />
                        <Divider sx={{ my: 1.5 }} />
                        <List dense disablePadding sx={dashboardModuleListScrollSx}>
                            {filteredNotes.map((n) => (
                                <ListItemButton
                                    key={String(n.id)}
                                    component="a"
                                    href={appRouteHref(`/notes/${n.id}/edit`)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={dashboardListLinkSx}
                                >
                                    <ListItemText
                                        primary={n.name || `Note ${n.id}`}
                                        secondary={stripHtml(n.description ?? '').slice(0, 72)}
                                    />
                                </ListItemButton>
                            ))}
                            {filteredNotes.length === 0 && (
                                <Box sx={{ py: 1 }}>
                                    <Typography variant="body2" color="warning.main" fontWeight={500}>
                                        No matches.
                                    </Typography>
                                </Box>
                            )}
                        </List>
                    </CardContent>
                </Card>

                <Card
                    variant="outlined"
                    sx={{
                        ...previewCardSearchBorderSx(
                            !!(globalSearch.trim() || tasksSearch.trim()),
                            filteredTasks.length
                        ),
                        ...previewCardRowSx,
                    }}
                >
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1, gap: 0.5 }}>
                            <Typography variant="subtitle1" component="div" sx={{ fontWeight: 700, lineHeight: 1.2, minWidth: 0 }} noWrap>
                                Tasks
                            </Typography>
                            <Button
                                component={RouterLink}
                                to="/tasks"
                                size="small"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Open
                            </Button>
                        </Stack>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search tasks"
                            value={tasksSearch}
                            onChange={(e) => setTasksSearch(e.target.value)}
                            InputProps={{
                                endAdornment: searchClearEndAdornment(tasksSearch, () => setTasksSearch('')),
                            }}
                        />
                        <Divider sx={{ my: 1.5 }} />
                        <List dense disablePadding sx={dashboardModuleListScrollSx}>
                            {filteredTasks.map((task) => (
                                <ListItemButton
                                    key={String(task.id)}
                                    component="a"
                                    href={appRouteHref(`/tasks?taskId=${encodeURIComponent(String(task.id))}`)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={dashboardListLinkSx}
                                >
                                    <ListItemText
                                        primary={task.name || `Task ${task.id}`}
                                        secondary={stripHtml(task.description ?? '').slice(0, 72)}
                                    />
                                </ListItemButton>
                            ))}
                            {filteredTasks.length === 0 && (
                                <Box sx={{ py: 1 }}>
                                    <Typography variant="body2" color="warning.main" fontWeight={500}>
                                        No matches.
                                    </Typography>
                                </Box>
                            )}
                        </List>
                    </CardContent>
                </Card>

                <Card
                    variant="outlined"
                    sx={{
                        ...previewCardSearchBorderSx(
                            !!(globalSearch.trim() || projectsSearch.trim()),
                            filteredProjects.length
                        ),
                        ...previewCardRowSx,
                    }}
                >
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1, gap: 0.5 }}>
                            <Typography variant="subtitle1" component="div" sx={{ fontWeight: 700, lineHeight: 1.2, minWidth: 0 }} noWrap>
                                Projects
                            </Typography>
                            <Button
                                component={RouterLink}
                                to="/projects"
                                size="small"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Open
                            </Button>
                        </Stack>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search projects"
                            value={projectsSearch}
                            onChange={(e) => setProjectsSearch(e.target.value)}
                            InputProps={{
                                endAdornment: searchClearEndAdornment(projectsSearch, () => setProjectsSearch('')),
                            }}
                        />
                        <Divider sx={{ my: 1.5 }} />
                        <List dense disablePadding sx={dashboardModuleListScrollSx}>
                            {filteredProjects.map((p) => (
                                <ListItemButton
                                    key={String(p.id)}
                                    component="a"
                                    href={appRouteHref(`/projects?projectId=${encodeURIComponent(String(p.id))}`)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={dashboardListLinkSx}
                                >
                                    <ListItemText
                                        primary={p.name || `Project ${p.id}`}
                                        secondary={(p.description ?? '').slice(0, 72)}
                                    />
                                </ListItemButton>
                            ))}
                            {filteredProjects.length === 0 && (
                                <Box sx={{ py: 1 }}>
                                    <Typography variant="body2" color="warning.main" fontWeight={500}>
                                        No matches.
                                    </Typography>
                                </Box>
                            )}
                        </List>
                    </CardContent>
                </Card>

                <Card
                    variant="outlined"
                    sx={{
                        ...previewCardSearchBorderSx(
                            !!(globalSearch.trim() || pathOpenerSearch.trim()),
                            filteredPathOpenerItems.length
                        ),
                        ...previewCardRowSx,
                    }}
                >
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1, gap: 0.5 }}>
                            <Typography variant="subtitle1" component="div" sx={{ fontWeight: 700, lineHeight: 1.2, minWidth: 0 }} noWrap>
                                Path Opener
                            </Typography>
                            <Button
                                component={RouterLink}
                                to="/path-opener"
                                size="small"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Open
                            </Button>
                        </Stack>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search paths (value, name, description, mode)"
                            value={pathOpenerSearch}
                            onChange={(e) => setPathOpenerSearch(e.target.value)}
                            InputProps={{
                                endAdornment: searchClearEndAdornment(pathOpenerSearch, () => setPathOpenerSearch('')),
                            }}
                        />
                        <Divider sx={{ my: 1.5 }} />
                        <List dense disablePadding sx={dashboardModuleListScrollSx}>
                            {filteredPathOpenerItems.map((item) => (
                                <ListItemButton
                                    key={String(item.id)}
                                    component="a"
                                    href={pathOpenerHref(item.value)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{ ...dashboardListLinkSx, pr: { xs: 8, sm: 9 } }}
                                    onClick={(e) => handlePathOpenerLinkClick(e, item.value)}
                                >
                                    <ListItemText
                                        primary={
                                            (item.name ?? '').trim()
                                                ? `${(item.name ?? '').trim()} — ${item.value || `Entry ${item.id}`}`
                                                : item.value || `Entry ${item.id}`
                                        }
                                        secondary={(() => {
                                            const desc = (item.description ?? '').trim();
                                            const descShort =
                                                desc.length > 90 ? `${desc.slice(0, 90)}…` : desc;
                                            return [
                                                desc ? descShort : null,
                                                `Mode: ${item.mode ?? 'Auto'}${typeof item.position === 'number' ? ` · Pos: ${item.position}` : ''}`,
                                            ]
                                                .filter(Boolean)
                                                .join(' · ');
                                        })()}
                                    />
                                    <ListItemSecondaryAction>
                                        <Tooltip title="Copy path">
                                            <IconButton
                                                edge="end"
                                                size="small"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    void copyToClipboard(item.value);
                                                }}
                                            >
                                                <ContentCopyIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title={isHttpUrl(item.value) ? 'Open in new tab' : 'Try open via file:// (Windows, best effort)'}>
                                            <IconButton
                                                edge="end"
                                                size="small"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    openPathOpenerValue(item.value);
                                                }}
                                            >
                                                {isHttpUrl(item.value) ? <LaunchIcon fontSize="small" /> : <FolderOpenIcon fontSize="small" />}
                                            </IconButton>
                                        </Tooltip>
                                    </ListItemSecondaryAction>
                                </ListItemButton>
                            ))}
                            {filteredPathOpenerItems.length === 0 && (
                                <Box sx={{ py: 1 }}>
                                    <Typography variant="body2" color="warning.main" fontWeight={500}>
                                        No matches.
                                    </Typography>
                                </Box>
                            )}
                        </List>
                    </CardContent>
                </Card>

                <Card
                    variant="outlined"
                    sx={{
                        ...previewCardSearchBorderSx(
                            !!(globalSearch.trim() || externalAssetsSearch.trim()),
                            filteredExternalAssets.length
                        ),
                        ...previewCardRowSx,
                    }}
                >
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1, gap: 0.5 }}>
                            <Typography variant="subtitle1" component="div" sx={{ fontWeight: 700, lineHeight: 1.2, minWidth: 0 }} noWrap>
                                External Assets
                            </Typography>
                            <Button
                                component={RouterLink}
                                to="/external-assets"
                                size="small"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Open
                            </Button>
                        </Stack>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search assets (title, path, URL, tags, notes, …)"
                            value={externalAssetsSearch}
                            onChange={(e) => setExternalAssetsSearch(e.target.value)}
                            InputProps={{
                                endAdornment: searchClearEndAdornment(externalAssetsSearch, () =>
                                    setExternalAssetsSearch('')
                                ),
                            }}
                        />
                        <Divider sx={{ my: 1.5 }} />
                        <List dense disablePadding sx={dashboardModuleListScrollSx}>
                            {filteredExternalAssets.map((asset) => {
                                const secondaryParts = [asset.publisher, asset.asset_type, asset.file_path, asset.url]
                                    .map((s) => (s ?? '').trim())
                                    .filter(Boolean);
                                const secondary = secondaryParts.join(' · ').slice(0, 72);
                                return (
                                    <ListItemButton
                                        key={String(asset.id)}
                                        component="a"
                                        href={appRouteHref(`/external-assets?assetId=${encodeURIComponent(String(asset.id))}`)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={dashboardListLinkSx}
                                    >
                                        <ListItemText
                                            primary={asset.title?.trim() || `Asset ${asset.id}`}
                                            secondary={secondary || '—'}
                                        />
                                    </ListItemButton>
                                );
                            })}
                            {filteredExternalAssets.length === 0 && (
                                <Box sx={{ py: 1 }}>
                                    <Typography variant="body2" color="warning.main" fontWeight={500}>
                                        No matches.
                                    </Typography>
                                </Box>
                            )}
                        </List>
                    </CardContent>
                </Card>
            </Box>
            )}
        </Container>
    );
}; 
